import { useState, useEffect } from 'react'
import { CardElement } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import Big from 'big.js'

import { genericError } from './useRecord'
import useStripeServices, { subscriptionIntervals } from './useStripeServices'
import useRecaptcha from './useRecaptcha'
import useAppleTouchIcons from './useAppleTouchIcons'
import useModalTakeover from 'src/widgets/useModalTakeover'
import walletEmailError from 'src/widgets/walletEmailError'
import csrfHeaders from 'src/utils/csrfHeaders'

const traceVersion = '2026-05-01T00:02:09Z'

const paymentRequestAmount = ({ amount, currency }) =>
  parseInt(Big(amount || 0).times(currency?.subunit_to_unit || 1).toString())

const paymentRequestProps = ({ i18n, partnerItem, donation, canMakePayment, creditCardProps }) => {
  const tipPresent = !!Number(donation.tip)

  const displayItems = [{
    label: i18n.t(`subscription_labels.${donation.state.subscription}.lineItem`),
    amount: paymentRequestAmount({
      amount: donation.state.amount,
      currency: donation.currency
    }),
  }]

  if (tipPresent)
    displayItems.push({
      label:  i18n.t(`subscription_labels.${donation.state.subscription}.tipToPledge`),
      amount: paymentRequestAmount({
        amount: donation.tip,
        currency: donation.currency,
      })
    })
  else if (donation.state.coverCosts)
    displayItems.push({
      label:  i18n.t('credit_card_fees'),
      amount: paymentRequestAmount({
        amount: donation.fees,
        currency: donation.currency,
      }),
    })

  return {
    currency: donation.currency.iso_code.toLowerCase(),
    displayItems,
    total: {
      label: canMakePayment?.applePay
      ? creditCardProps.directlyDisbursed
      ? partnerItem.beneficiary.name
      : i18n.t('via_pledge', { org: partnerItem.beneficiary.name })
      : i18n.t('total'),
      amount: paymentRequestAmount({
        amount: donation.total,
        currency: donation.currency,
      })
    },
  }
}

const resolveWalletName = walletName => {
  switch (walletName) {
    case 'applePay':  return 'Apple Pay'
    case 'googlePay': return 'Google Pay'
    case 'link':      return 'Link'
    default:          return 'Payment Request'
  }
}

const useCreditCard = ({
  i18n, partnerItem, donation, record, tribute, creditCardEnabled,
  stripe, elements, collect_phone, validateAll, creditCardProps,
  finished, failed: upstreamFailed,
  enable_upsell, stripeKey, disable_express_checkout,
}) => {
  // undefined while wallet detection is still in flight, null once it
  // has settled with no wallet available — fiat/App.jsx holds the whole
  // payment-option list back while it's undefined. The PWC kill switch
  // (data.disable_express_checkout, which also turns off v7's Express
  // Checkout Element) makes the answer known before we ask, so start
  // out settled; the effect below then skips detection entirely.
  const [ canMakePayment, setCanMakePayment ] =
    useState(disable_express_checkout ? null : undefined)
  const [ paymentRequest, setPaymentRequest ] = useState(null)
  const { normalizeStripeError, normalizeServerError, createIntent } =
    useStripeServices({ ...creditCardProps, i18n })
  // Full-screens the widget's host-page <dialog> while a 3DS challenge
  // is up. No-op unless the widget is embedded and a visible Stripe
  // challenge frame appears. Opt-in per PWC (data.fullscreen_3ds), so
  // off unless a partner has it switched on.
  const takeover = useModalTakeover({ enabled: creditCardProps.fullscreen_3ds === true })

  useAppleTouchIcons(partnerItem.beneficiary.logo_img)

  // Both terminal paths release the takeover (no-op when none is
  // active). `failed` covers every failure; `finish` wraps the success
  // hand-off so the host dialog drops back inline before the donor
  // lands on the confirmation.
  const failed = errors => {
    takeover.release()
    upstreamFailed(errors)
    recaptcha.reset()
  }

  const finish = (...args) => {
    takeover.release()
    finished(...args)
  }

  const updateDonation = (token, body, tracePath) => {
    const url = [ creditCardProps.stripeIntentsPath, token ].join('/')
    const trace_path = [ tracePath || 'UNSPECIFIED', traceVersion ].join('/')
    const options = {
      method: 'PATCH',
      headers: csrfHeaders(),
      body: JSON.stringify({ locale: i18n.locale, ...body, trace_path }),
    }
    return fetch(url, options)
  }

  const confirmOnConnectedAccount = async ({ client_secret, stripe_account, token, serializedDonation }) => {
    try {
      const stripeInstance = stripe_account
        ? await loadStripe(stripeKey, { stripeAccount: stripe_account, locale: i18n.locale })
        : stripe

      const { error, paymentIntent: intent } = await stripeInstance.confirmCardPayment(client_secret)
      if (error) return failed(normalizeStripeError(error))
      if (intent.status !== 'succeeded') return failed(genericError(i18n))

      // Second PATCH: for the upsell flow the controller hits the card_payment_intent?
      // path for fulfillment; for the subscription 3DS flow the backend resolves
      // status from the existing subscription. Fulfillment may also happen via webhook.
      updateDonation(token, { enable_upsell }, 'connected_account')
      finish({ token, serializedDonation })
    } catch (error) {
      console.error(error)
      failed(genericError(i18n))
    }
  }

  const finalizeIntent = async ({ intent, intent_type, token, serializedDonation }, tracePath) => {
    try {
      const response = await updateDonation(token, {
        payment_method_id: (pm => pm.id || pm)(intent.payment_method),
        enable_upsell,
      }, tracePath)

      if (response.status != 204) {
        const json = await response.json()
        if (!response.ok && intent_type != 'payment_intent')
          return failed(normalizeServerError(json))

        // Subscription 3DS or upsell flow: backend returned a client_secret
        // that needs on-session frontend confirmation. stripe_account is only
        // present for the upsell path; subscription 3DS uses the existing stripe instance.
        if (json.client_secret)
          return confirmOnConnectedAccount({ ...json, token, serializedDonation })
      }

      finish({ token, serializedDonation })
    } catch (error) {
      console.error(error)
      if (intent_type == 'payment_intent')
        finish({ token, serializedDonation })
      else
        failed(genericError(i18n))
    }
  }

  const confirmIntent = async (intent_type, ...args) => {
    switch (intent_type) {
      case 'setup_intent': {
        const { error, setupIntent } = await stripe.confirmCardSetup(...args)
        return { error, intent: setupIntent }
      }
      case 'payment_intent': {
        const { error, paymentIntent } = await stripe.confirmCardPayment(...args)
        return { error, intent: paymentIntent }
      }
      default: throw new Error(`Unknown intent type: ${intent_type}`)
    }
  }

  // Resolves a confirmed intent to `succeeded`, retrying once if action is required.
  // confirmCardPayment can return requires_action even with handleActions:true (e.g. when
  // the 3DS modal is blocked by iframe CSP). The retry gives Stripe.js a second chance.
  // Returns { intent, depth } on success, { error } when retries are exhausted,
  // or { error, unexpectedStatus, depth } for undocumented statuses — callers should
  // fire a best-effort backend ping in that case so we can observe it.
  const resolveConfirmedIntent = async (confirmFn, intent, depth = 0) => {
    switch (intent.status) {
      case 'succeeded': return { intent, depth }
      case 'requires_action':
      case 'requires_source_action': // deprecated alias — observed live on Google Pay
        if (depth === 0) {
          const { error, intent: nextIntent } = await confirmFn()
          if (error) return { error: normalizeStripeError(error) }
          return resolveConfirmedIntent(confirmFn, nextIntent, 1)
        }
        // depth >= 1: retries exhausted — expected failure, no ping needed
        return { error: genericError(i18n) }
      default:
        // undocumented terminal status — show error normally but ping backend to observe it
        return { error: genericError(i18n), unexpectedStatus: intent.status, depth }
    }
  }

  const makePaymentRequest = async ({
    complete, paymentMethod, walletName, payerName, payerEmail, payerPhone
  }) => {
    // Checked ahead of saveDraft: Contact isn't rendered for the wallet
    // option, and PaymentOptions only shows base errors, so Order's
    // rejection would fail the sheet with nothing on screen. Trimmed first
    // and sent trimmed; see walletEmailError.
    const email = payerEmail?.trim()
    const emailError = walletEmailError({ i18n, email, wallet: resolveWalletName(walletName) })
    if (emailError) {
      complete('invalid_payer_email')
      return failed(emailError)
    }

    record.setProcessing(true)
    takeover.startListening()

    const name = (payerName || '').split(' ')
    const contact = {
      firstName:  name.at(0) || 'N/A',
      lastName:   name.at(-1) || 'N/A',
      email,
      phone:      payerPhone,
    }

    if (donation.collectAddress)
      contact.address = paymentMethod.billing_details.address

    const subscription = subscriptionIntervals[donation.state.subscription]

    const { errors: draftErrors, token, serializedDonation } = await record.saveDraft({
      contact,
      order: { payment_source: 'stripe_card', payment_method_used: resolveWalletName(walletName) },
    })

    if (draftErrors) {
      complete('fail')
      return failed(draftErrors)
    }

    const { errors: intentErrors, client_secret, intent_type } =
      await createIntent(token, {
        payment_method_id: paymentMethod.id,
        subscription,
        enable_upsell,
      })

    if (intentErrors) {
      complete('fail')
      return failed(intentErrors)
    }

    // Confirm without handling actions so we can close the payment sheet first
    const { error, intent: initial } = await confirmIntent(intent_type,
      client_secret,
      { payment_method: paymentMethod.id },
      { handleActions: false }
    )

    if (error) {
      complete('fail')
      return failed(normalizeStripeError(error))
    }

    complete('success')

    const { error: resolveError, intent, depth, unexpectedStatus } = await resolveConfirmedIntent(
      () => confirmIntent(intent_type, client_secret),
      initial
    )

    if (resolveError) {
      if (unexpectedStatus !== undefined)
        updateDonation(token, { enable_upsell }, `curious/${unexpectedStatus}/payment_request/${depth}`)
      return failed(resolveError)
    }
    finalizeIntent({ intent, intent_type, token, serializedDonation }, `payment_request/${depth}`)
  }

  const getPaymentRequestProps = () =>
    paymentRequestProps({ i18n, partnerItem, donation, canMakePayment, creditCardProps })

  const openPaymentSheet = e => {
    if (!validateAll({ contact: false }))
      e.preventDefault()
    else {
      paymentRequest.update(getPaymentRequestProps())
      return true
    }
  }

  useEffect(() => {
    if (!stripe || !creditCardEnabled || disable_express_checkout) return

    const pr = stripe.paymentRequest({
      country:            'US',
      requestPayerName:   true,
      requestPayerEmail:  true,
      requestPayerPhone:  !!collect_phone, // must be boolean
      ...getPaymentRequestProps(),
    })

    let cancelled = false
    pr.canMakePayment().then(result => {
      if (cancelled) return
      if (result?.applePay || result?.googlePay) {
        setPaymentRequest(pr)
        setCanMakePayment(result)
      } else {
        console.warn('No supported wallet payment methods available')
        setCanMakePayment(null)
      }
    }).catch(error => {
      if (!cancelled) {
        console.error(error)
        setCanMakePayment(null)
      }
    })

    return () => { cancelled = true }
  }, [ stripe, creditCardEnabled, disable_express_checkout ])

  useEffect(() => {
    if (!paymentRequest || !canMakePayment) return

    paymentRequest.on('paymentmethod', makePaymentRequest)
    return () => paymentRequest.off('paymentmethod', makePaymentRequest)
  }, [ paymentRequest, canMakePayment, partnerItem, donation, tribute ])

  const payWithCard = async captcha_token => {
    record.setProcessing(true)
    takeover.startListening()

    const subscription = subscriptionIntervals[donation.state.subscription]

    const { errors: draftErrors, token, serializedDonation } =
      await record.saveDraft({ order: { payment_source: 'stripe_card', payment_method_used: 'Credit Card' } })

    if (draftErrors) return failed(draftErrors)

    // Virtual terminal (MOTO): a phone-bank operator keyed the caller's card.
    // MOTO is a server-side assertion Stripe only accepts at confirmation, so
    // instead of confirming client-side (below) we tokenize the card and hand
    // the PaymentMethod id to the server, which create-and-confirms with moto.
    if (creditCardProps.virtualTerminal) {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name: [ donation.state.firstName, donation.state.lastName ].join(' '),
        },
      })
      if (pmError) return failed(normalizeStripeError(pmError))

      // Reuses the create endpoint — the backend routes a virtual-terminal
      // order with a payment_method_id to the MOTO confirm, answering 204 on
      // success or the usual error shape on a decline / issuer-forced 3DS.
      const { errors: motoErrors } = await createIntent(token, {
        payment_method_id: paymentMethod.id,
        moto: true,
        captcha_token,
      })
      if (motoErrors) return failed(motoErrors)

      return finish({ token, serializedDonation })
    }

    const { errors: intentErrors, client_secret, intent_type } =
      await createIntent(token, { captcha_token, subscription, enable_upsell })

    if (intentErrors) return failed(intentErrors)

    const params = {
      payment_method: {
        card: elements.getElement(CardElement),
        billing_details: {
          name: [ donation.state.firstName, donation.state.lastName ].join(' '),
        }
      }
    }

    const { error, intent: initial } = await confirmIntent(intent_type, client_secret, params)
    if (error) return failed(normalizeStripeError(error))

    // confirmCardPayment normally handles actions internally, but in embedded iframes
    // CSP/sandboxing can prevent the 3DS modal from loading, causing it to return
    // requires_action instead. resolveConfirmedIntent retries once to give Stripe.js
    // a second chance to surface the challenge (or fail cleanly without pinging the backend).
    const { error: resolveError, intent, depth, unexpectedStatus } = await resolveConfirmedIntent(
      () => confirmIntent(intent_type, client_secret),
      initial
    )

    if (resolveError) {
      if (unexpectedStatus !== undefined)
        updateDonation(token, { enable_upsell }, `curious/${unexpectedStatus}/credit_card/${depth}`)
      return failed(resolveError)
    }
    finalizeIntent({ intent, intent_type, token, serializedDonation }, `credit_card/${depth}`)
  }

  const recaptcha = useRecaptcha({
    enabled: creditCardProps.recaptcha_enabled,
    siteKey: creditCardProps.recaptchaSiteKey,
    success: payWithCard,
  })

  const process = () => {
    if (recaptcha.available)
      recaptcha.execute()
    else
      payWithCard()
  }

  return {
    canMakePayment,
    paymentRequest,
    openPaymentSheet,
    process,
    renderRecaptcha: recaptcha.renderElement,
  }
}

export default useCreditCard
